import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import moment from "moment";
import styled from "styled-components";
import { CommonTableHeadCell } from "../../controls/components/commonTable/CommonTable";
import NumberPaginationBox from "../../controls/components/numberPaginationBox/NumberPaginationBox";
import {
	BookingContentName,
	BookingContentWrap,
	MyBookingTableContainerAdmin,
	MyBookingWrapper,
	PaginationWrapper,
	PaginationWrapperTop,
} from "../MyBooking/styled/DesStyled";
import {
	AdminServiceInterestAnalytics,
	AdminServiceInterestRecord,
	AdminServiceInterestSummary,
	getAdminServiceInterestClicks,
} from "../../../services/ServiceInterestService";

const PER_PAGE = 20;

const SERVICE_META: Record<
	string,
	{
		label: string;
		color: string;
		accent: string;
		description: string;
	}
> = {
	esim: {
		label: "eSIM service",
		color: "#1D4ED8",
		accent: "#DBEAFE",
		description: "Connectivity demand after booking",
	},
	airport: {
		label: "Airport service",
		color: "#0F766E",
		accent: "#CCFBF1",
		description: "Airport-support demand after booking",
	},
};

const EMPTY_ANALYTICS: AdminServiceInterestAnalytics = {
	totalUniqueRequests: 0,
	uniqueBookings: 0,
	byService: [],
	byStation: [],
	dailyTrend: [],
	recentIntents: [],
};

const StatsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
	gap: 16px;
	margin-bottom: 24px;
`;

const StatCard = styled.div<{ $accent: string }>`
	background: #ffffff;
	border-radius: 18px;
	padding: 22px;
	box-shadow: 0px 4px 14.6px rgba(0, 0, 0, 0.08);
	border: 1px solid ${(props) => props.$accent};
`;

const StatLabel = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	margin-bottom: 10px;
`;

const StatValue = styled.div`
	font-size: 34px;
	font-weight: 700;
	line-height: 1;
	color: #111827;
	margin-bottom: 8px;
`;

const StatHint = styled.div`
	font-size: 14px;
	color: #4b5563;
	line-height: 1.5;
`;

const AnalyticsGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
	gap: 18px;
	margin-bottom: 24px;

	@media (max-width: 1200px) {
		grid-template-columns: 1fr;
	}
`;

const DonutLayout = styled.div`
	display: grid;
	grid-template-columns: 220px minmax(0, 1fr);
	gap: 20px;
	align-items: center;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

const DonutChartWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const DonutChart = styled.div<{ $background: string }>`
	width: 220px;
	height: 220px;
	border-radius: 50%;
	background: ${(props) => props.$background};
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.55);
`;

const DonutHole = styled.div`
	width: 124px;
	height: 124px;
	border-radius: 50%;
	background: #ffffff;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	box-shadow: 0px 6px 20px rgba(15, 23, 42, 0.08);
	padding: 14px;
`;

const DonutCenterLabel = styled.div`
	font-size: 12px;
	font-weight: 700;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.08em;
`;

const DonutCenterValue = styled.div`
	font-size: 32px;
	font-weight: 800;
	color: #111827;
	line-height: 1.1;
	margin-top: 6px;
`;

const LegendList = styled.div`
	display: grid;
	gap: 12px;
`;

const LegendRow = styled.div`
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto;
	gap: 12px;
	align-items: center;
	padding: 10px 12px;
	border: 1px solid #e5e7eb;
	border-radius: 14px;
`;

const LegendSwatch = styled.div<{ $color: string }>`
	width: 12px;
	height: 12px;
	border-radius: 999px;
	background: ${(props) => props.$color};
`;

const LegendLabel = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #111827;
`;

const LegendValue = styled.div`
	font-size: 13px;
	font-weight: 700;
	color: #374151;
	white-space: nowrap;
`;

const Panel = styled.div`
	background: #ffffff;
	border-radius: 18px;
	padding: 22px;
	box-shadow: 0px 4px 14.6px rgba(0, 0, 0, 0.08);
`;

const PanelHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 18px;
`;

const PanelTitle = styled.h3`
	margin: 0;
	font-size: 20px;
	font-weight: 700;
	color: #111827;
`;

const PanelHint = styled.p`
	margin: 6px 0 0;
	font-size: 14px;
	color: #6b7280;
	line-height: 1.5;
`;

const HighlightBadge = styled.div`
	padding: 8px 12px;
	border-radius: 999px;
	background: #f3f4f6;
	font-size: 12px;
	font-weight: 700;
	color: #374151;
	white-space: nowrap;
`;

const StationName = styled.div`
	font-size: 15px;
	font-weight: 600;
	color: #111827;
`;

const StationAddress = styled.div`
	font-size: 13px;
	color: #6b7280;
	margin-top: 4px;
`;

const TrendList = styled.div`
	display: grid;
	gap: 12px;
`;

const TrendRow = styled.div`
	display: grid;
	grid-template-columns: 92px minmax(0, 1fr) auto;
	gap: 12px;
	align-items: center;
`;

const TrendLabel = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #374151;
`;

const TrendStack = styled.div`
	display: flex;
	gap: 4px;
	height: 10px;
	border-radius: 999px;
	overflow: hidden;
	background: #f3f4f6;
`;

const TrendSegment = styled.div<{ $width: number; $color: string }>`
	width: ${(props) => Math.max(props.$width, 0)}%;
	background: ${(props) => props.$color};
`;

const TrendValue = styled.div`
	font-size: 13px;
	font-weight: 700;
	color: #111827;
`;

const RecentList = styled.div`
	display: grid;
	gap: 12px;
`;

const RecentCard = styled.div`
	border-radius: 14px;
	border: 1px solid #e5e7eb;
	padding: 14px 16px;
	display: grid;
	gap: 6px;
`;

const RecentTop = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 12px;
	align-items: center;
`;

const RecentCustomer = styled.div`
	font-size: 15px;
	font-weight: 600;
	color: #111827;
`;

const RecentTimestamp = styled.div`
	font-size: 12px;
	color: #6b7280;
	white-space: nowrap;
`;

const RecentMeta = styled.div`
	font-size: 13px;
	color: #4b5563;
	line-height: 1.5;
`;

const ServiceBadge = styled.span<{ $color: string; $accent: string }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 4px 10px;
	border-radius: 999px;
	background: ${(props) => props.$accent};
	color: ${(props) => props.$color};
	font-size: 12px;
	font-weight: 700;
`;

const DetailText = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 6px;
	line-height: 1.45;
`;

const EmptyState = styled.div`
	padding: 28px 0;
	text-align: center;
	color: #6b7280;
	font-size: 14px;
`;

const STATION_DONUT_COLORS = ["#111827", "#2563EB", "#0F766E", "#7C3AED", "#EA580C", "#DC2626"];

function getServiceMeta(serviceType: string) {
	return (
		SERVICE_META[serviceType] || {
			label: serviceType,
			color: "#374151",
			accent: "#E5E7EB",
			description: "Tracked service interest",
		}
	);
}

function formatMoney(totalMoney?: number | null) {
	return `${(Math.round((totalMoney || 0) * 100) / 100).toFixed(2)} USD`;
}

function buildDonutBackground(segments: Array<{ value: number; color: string }>) {
	const validSegments = segments.filter((segment) => segment.value > 0);
	const total = validSegments.reduce((sum, segment) => sum + segment.value, 0);

	if (!total) {
		return "conic-gradient(#E5E7EB 0deg 360deg)";
	}

	let currentAngle = 0;
	const stops = validSegments.map((segment) => {
		const start = currentAngle;
		currentAngle += (segment.value / total) * 360;
		return `${segment.color} ${start}deg ${currentAngle}deg`;
	});

	return `conic-gradient(${stops.join(", ")})`;
}

export default function ServiceInterestClicksPage() {
	const [docs, setDocs] = useState<AdminServiceInterestRecord[]>([]);
	const [summary, setSummary] = useState<AdminServiceInterestSummary[]>([]);
	const [analytics, setAnalytics] = useState<AdminServiceInterestAnalytics>(EMPTY_ANALYTICS);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		let mounted = true;

		const load = async () => {
			setLoading(true);
			try {
				const response = await getAdminServiceInterestClicks({ page, perPage: PER_PAGE });
				if (!mounted) return;
				setDocs(response.docs);
				setSummary(response.summary);
				setAnalytics(response.analytics || EMPTY_ANALYTICS);
				setTotal(response.total);
			} catch (error) {
				if (!mounted) return;
				console.error(error);
				setDocs([]);
				setSummary([]);
				setAnalytics(EMPTY_ANALYTICS);
				setTotal(0);
			} finally {
				if (mounted) {
					setLoading(false);
				}
			}
		};

		load();
		return () => {
			mounted = false;
		};
	}, [page]);

	const summaryMap = useMemo(() => {
		return summary.reduce<Record<string, number>>((acc, item) => {
			acc[item.serviceType] = item.totalRequests;
			return acc;
		}, {});
	}, [summary]);

	const leadingService = useMemo(() => {
		return [...analytics.byService].sort((a, b) => b.totalRequests - a.totalRequests)[0];
	}, [analytics.byService]);
	const serviceDonutSegments = useMemo(() => {
		return analytics.byService.map((item) => {
			const meta = getServiceMeta(item.serviceType);
			return {
				key: item.serviceType,
				label: meta.label,
				value: item.totalRequests,
				share: item.share,
				color: meta.color,
			};
		});
	}, [analytics.byService]);
	const stationDonutSegments = useMemo(() => {
		const topStations = analytics.byStation.slice(0, 5).map((station, index) => ({
			key: `${station.stationName}-${index}`,
			label: station.stationName,
			value: station.totalRequests,
			share: station.share,
			color: STATION_DONUT_COLORS[index % STATION_DONUT_COLORS.length],
		}));
		const topTotal = topStations.reduce((sum, item) => sum + item.value, 0);
		const otherValue = Math.max(analytics.totalUniqueRequests - topTotal, 0);

		if (otherValue > 0) {
			topStations.push({
				key: "other",
				label: "Other stations",
				value: otherValue,
				share: analytics.totalUniqueRequests
					? Math.round((otherValue / analytics.totalUniqueRequests) * 1000) / 10
					: 0,
				color: "#9CA3AF",
			});
		}

		return topStations;
	}, [analytics.byStation, analytics.totalUniqueRequests]);

	const strongestStation = analytics.byStation[0];
	const maxTrendValue = Math.max(...analytics.dailyTrend.map((item) => item.totalRequests), 1);

	return (
		<MyBookingWrapper>
			<BookingContentWrap>
				<BookingContentName>Service interest dashboard</BookingContentName>

				<StatsGrid>
					<StatCard $accent="#E5E7EB">
						<StatLabel>Total unique requests</StatLabel>
						<StatValue>{analytics.totalUniqueRequests}</StatValue>
						<StatHint>Only the first click of each booking-service pair is counted.</StatHint>
					</StatCard>
					<StatCard $accent="#E5E7EB">
						<StatLabel>Unique bookings</StatLabel>
						<StatValue>{analytics.uniqueBookings}</StatValue>
						<StatHint>Bookings that showed interest in at least one add-on service.</StatHint>
					</StatCard>
					<StatCard $accent={SERVICE_META.esim.accent}>
						<StatLabel>{SERVICE_META.esim.label}</StatLabel>
						<StatValue>{summaryMap.esim || 0}</StatValue>
						<StatHint>{SERVICE_META.esim.description}</StatHint>
					</StatCard>
					<StatCard $accent={SERVICE_META.airport.accent}>
						<StatLabel>{SERVICE_META.airport.label}</StatLabel>
						<StatValue>{summaryMap.airport || 0}</StatValue>
						<StatHint>{SERVICE_META.airport.description}</StatHint>
					</StatCard>
				</StatsGrid>

				<AnalyticsGrid>
					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Service mix</PanelTitle>
								<PanelHint>Which add-on customers care about most right after checkout.</PanelHint>
							</div>
							<HighlightBadge>
								Leader: {leadingService ? getServiceMeta(leadingService.serviceType).label : "No data"}
							</HighlightBadge>
						</PanelHeader>
						{serviceDonutSegments.length > 0 ? (
							<DonutLayout>
								<DonutChartWrap>
									<DonutChart $background={buildDonutBackground(serviceDonutSegments)}>
										<DonutHole>
											<DonutCenterLabel>Requests</DonutCenterLabel>
											<DonutCenterValue>{analytics.totalUniqueRequests}</DonutCenterValue>
										</DonutHole>
									</DonutChart>
								</DonutChartWrap>
								<LegendList>
									{serviceDonutSegments.map((item) => (
										<LegendRow key={item.key}>
											<LegendSwatch $color={item.color} />
											<LegendLabel>{item.label}</LegendLabel>
											<LegendValue>
												{item.value} . {item.share}%
											</LegendValue>
										</LegendRow>
									))}
								</LegendList>
							</DonutLayout>
						) : (
							<EmptyState>No service interest data yet.</EmptyState>
						)}
					</Panel>

					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Top stations</PanelTitle>
								<PanelHint>Stations generating the strongest post-booking service demand.</PanelHint>
							</div>
							<HighlightBadge>
								Top: {strongestStation ? strongestStation.stationName : "No data"}
							</HighlightBadge>
						</PanelHeader>
						{stationDonutSegments.length > 0 ? (
							<DonutLayout>
								<DonutChartWrap>
									<DonutChart $background={buildDonutBackground(stationDonutSegments)}>
										<DonutHole>
											<DonutCenterLabel>Stations</DonutCenterLabel>
											<DonutCenterValue>{analytics.byStation.length}</DonutCenterValue>
										</DonutHole>
									</DonutChart>
								</DonutChartWrap>
								<LegendList>
									{stationDonutSegments.map((station) => (
										<LegendRow key={station.key}>
											<LegendSwatch $color={station.color} />
											<div>
												<LegendLabel>{station.label}</LegendLabel>
												{station.label !== "Other stations" &&
													analytics.byStation.find((item) => item.stationName === station.label)?.stationAddress && (
														<StationAddress style={{ marginTop: 2 }}>
															{
																analytics.byStation.find((item) => item.stationName === station.label)
																	?.stationAddress
															}
														</StationAddress>
													)}
											</div>
											<LegendValue>
												{station.value} . {station.share}%
											</LegendValue>
										</LegendRow>
									))}
								</LegendList>
							</DonutLayout>
						) : (
							<EmptyState>No station signals yet.</EmptyState>
						)}
					</Panel>
				</AnalyticsGrid>

				<AnalyticsGrid>
					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Demand timeline</PanelTitle>
								<PanelHint>Daily unique interest captured over the last 14 days.</PanelHint>
							</div>
							<HighlightBadge>Window: 14 days</HighlightBadge>
						</PanelHeader>
						<TrendList>
							{analytics.dailyTrend.map((item) => {
								const totalWidth = (item.totalRequests / maxTrendValue) * 100;
								const esimWidth = item.totalRequests ? (item.esimRequests / item.totalRequests) * totalWidth : 0;
								const airportWidth = item.totalRequests
									? (item.airportRequests / item.totalRequests) * totalWidth
									: 0;
								return (
									<TrendRow key={item.date}>
										<TrendLabel>{moment(item.date).format("DD MMM")}</TrendLabel>
										<TrendStack>
											<TrendSegment $width={esimWidth} $color={SERVICE_META.esim.color} />
											<TrendSegment $width={airportWidth} $color={SERVICE_META.airport.color} />
										</TrendStack>
										<TrendValue>{item.totalRequests}</TrendValue>
									</TrendRow>
								);
							})}
							{analytics.dailyTrend.length === 0 && <EmptyState>No timeline data yet.</EmptyState>}
						</TrendList>
					</Panel>

					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Recent signals</PanelTitle>
								<PanelHint>The latest unique service-interest captures from customer bookings.</PanelHint>
							</div>
							<HighlightBadge>{analytics.recentIntents.length} latest records</HighlightBadge>
						</PanelHeader>
						<RecentList>
							{analytics.recentIntents.map((item) => {
								const meta = getServiceMeta(item.serviceType);
								return (
									<RecentCard key={item.id}>
										<RecentTop>
											<RecentCustomer>{item.customerName || "Unknown customer"}</RecentCustomer>
											<RecentTimestamp>{moment(item.clickedAt).format("HH:mm DD-MM-YYYY")}</RecentTimestamp>
										</RecentTop>
										<RecentMeta>
											Booking #{item.orderId} . {item.stationName || "Unknown station"}
										</RecentMeta>
										<div>
											<ServiceBadge $color={meta.color} $accent={meta.accent}>
												{meta.label}
											</ServiceBadge>
										</div>
									</RecentCard>
								);
							})}
							{analytics.recentIntents.length === 0 && <EmptyState>No recent signals yet.</EmptyState>}
						</RecentList>
					</Panel>
				</AnalyticsGrid>

				<PaginationWrapperTop>
					<NumberPaginationBox
						page={page}
						count={total}
						per={PER_PAGE}
						onChange={(nextPage) => setPage(nextPage)}
					/>
				</PaginationWrapperTop>

				<MyBookingTableContainerAdmin style={{ overflowX: "auto" }}>
					{loading ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 36 }}>
							<CircularProgress size={30} />
						</div>
					) : (
						<Table aria-label="service interest analytics table" style={{ minWidth: 1440 }}>
							<TableHead>
								<TableRow>
									<CommonTableHeadCell align="center">Captured at</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Service</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Customer</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Booking</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Station</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Signal</CommonTableHeadCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{docs.map((record) => {
									const partner = record.order?.partner;
									const meta = getServiceMeta(record.serviceType);
									const customerName =
										record.customerName ||
										record.order?.customer?.fullName ||
										record.customerEmail?.split("@")[0] ||
										"--";
									const customerEmail = record.customerEmail || record.order?.customer?.email || "--";
									const customerPhone = record.customerPhone || record.order?.customer?.phone || "--";
									const customerPhonePrefix =
										record.customerPhonePrefix || record.order?.customer?.phonePrefix || "--";

									return (
										<TableRow key={record.id}>
											<TableCell align="center">
												{moment(record.clickedAt).format("HH:mm DD-MM-YYYY")}
											</TableCell>
											<TableCell align="center">
												<ServiceBadge $color={meta.color} $accent={meta.accent}>
													{meta.label}
												</ServiceBadge>
											</TableCell>
											<TableCell align="left">
												<DetailText>
													<span>
														<strong>Name:</strong> {customerName}
													</span>
													<span>
														<strong>Email:</strong> {customerEmail}
													</span>
													<span>
														<strong>Phone:</strong> {customerPhone}
													</span>
													<span>
														<strong>Code:</strong> {customerPhonePrefix}
													</span>
												</DetailText>
											</TableCell>
											<TableCell align="left">
												<DetailText>
													<span>
														<strong>Booking ID:</strong> #{record.orderId}
													</span>
													<span>
														<strong>Bags:</strong> {record.order?.quantity || 0}
													</span>
													<span>
														<strong>Total:</strong> {formatMoney(record.order?.totalMoney)}
													</span>
													<span>
														<strong>Drop-off:</strong>{" "}
														{record.order?.timeInString
															? moment(record.order.timeInString).format("DD-MM-YYYY")
															: record.order?.timeIn
															? moment(record.order.timeIn).format("DD-MM-YYYY")
															: "--"}
													</span>
													<span>
														<strong>Pick-up:</strong>{" "}
														{record.order?.timeOutString
															? moment(record.order.timeOutString).format("DD-MM-YYYY")
															: record.order?.timeOut
															? moment(record.order.timeOut).format("DD-MM-YYYY")
															: "--"}
													</span>
												</DetailText>
											</TableCell>
											<TableCell align="left">
												<DetailText>
													<span>
														<strong>Name:</strong> {partner?.displayName || partner?.name || "--"}
													</span>
													<span>
														<strong>Address:</strong> {partner?.displayAddress || partner?.address || "--"}
													</span>
												</DetailText>
											</TableCell>
											<TableCell align="center">
												<HighlightBadge>Unique</HighlightBadge>
											</TableCell>
										</TableRow>
									);
								})}
								{docs.length === 0 && (
									<TableRow>
										<TableCell align="center" colSpan={6}>
											No unique service-interest signals recorded yet.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					)}
				</MyBookingTableContainerAdmin>

				<PaginationWrapper>
					<NumberPaginationBox
						page={page}
						count={total}
						per={PER_PAGE}
						onChange={(nextPage) => setPage(nextPage)}
					/>
				</PaginationWrapper>
			</BookingContentWrap>
		</MyBookingWrapper>
	);
}
